import React from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { usePortfolioProjects } from '../../../hooks/usePortfolioProjects';
import { NioSection, NioCard, NioIcon } from '../../../components';

const ext = { target: '_blank', rel: 'noopener noreferrer' };

function ProjectGridCard({ project, t }) {
  const visualSrc = project.photoUrl || project.logoSrc;
  const imgClass = project.photoUrl
    ? 'jassp-realisation-card-photo rounded-3'
    : 'portfolio-project-logo';

  return (
    <NioCard className="text-center h-100 portfolio-project-card jassp-realisation-grid-card">
      <NioCard.Body className="p-4 p-lg-5 d-flex flex-column">
        <div className="mb-3 d-flex justify-content-center">
          <img
            src={visualSrc}
            alt={project.logoAlt || project.title}
            className={imgClass}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="mb-3 flex-grow-1 text-start">
          <h5 className="mb-2 text-capitalize text-center">{project.title}</h5>
          <p className="fs-15 line-clamp-4 mb-3 text-gray-700">{project.description}</p>
          <p className="fs-13 mb-2 text-gray-600">
            <span className="fw-semibold text-dark">{t('portfolio.card.role')}</span>{' '}
            <span>{project.role}</span>
          </p>
          <p className="fs-13 mb-0 text-gray-600">
            <span className="fw-semibold text-dark">{t('portfolio.card.tech')}</span>{' '}
            <span>{project.technologies}</span>
          </p>
        </div>
        {project.websiteUrl?.trim() ? (
          <a href={project.websiteUrl.trim()} className="btn btn-sm btn-outline-primary mt-auto" {...ext}>
            {t('portfolio.card.visit')}
            <NioIcon name="arrow-right" className="ms-1" />
          </a>
        ) : null}
      </NioCard.Body>
    </NioCard>
  );
}

function Realisations() {
  const { t } = useLanguage();
  const { filters, activeFilter, setActiveFilter, visibleProjects } = usePortfolioProjects();

  return (
    <AppLayout variant={2} titleKey="pages.realisations.title" rootClass="layout-1">
      <div className="jassp-realisations-page">
        <section className="jassp-realisations-hero pt-7 pt-lg-120 pb-6 pb-lg-7">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={10} xl={9}>
                <h1 className="jassp-realisations-hero-title display-4 fw-bold mb-4">
                  {t('realisations.hero.h1')}
                </h1>
                <p className="jassp-realisations-hero-lead fs-18 mb-0 mx-auto">
                  {t('realisations.hero.leadBefore')}
                  <span className="jassp-realisations-hero-highlight">{t('realisations.hero.leadHighlight')}</span>
                  {t('realisations.hero.leadAfter')}
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        <NioSection className="jassp-hero-light-gap pb-7 pb-lg-120 pt-4 pt-lg-5 bg-light">
        <Container>
          <div className="portfolio-filter-bar d-flex flex-wrap align-items-center gap-2 mb-5 pb-2 border-bottom">
            <span className="fw-bold me-1">{t('realisations.filter.label')}</span>
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${
                  activeFilter === f.id ? 'btn-primary' : 'btn-outline-secondary'
                }`}
                onClick={() => setActiveFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {visibleProjects.length === 0 ? (
            <p className="text-center text-muted mb-0">{t('portfolio.empty')}</p>
          ) : (
            <Row className="gy-4">
              {visibleProjects.map((project) => (
                <Col key={project.id} md={6} lg={4}>
                  <ProjectGridCard project={project} t={t} />
                </Col>
              ))}
            </Row>
          )}

          <div className="text-center pt-5 mt-4">
            <Link to="/" className="btn btn-outline-primary">
              <NioIcon name="arrow-left" className="me-2" />
              {t('portfolio.backHome')}
            </Link>
          </div>
        </Container>
        </NioSection>
      </div>
    </AppLayout>
  );
}

export default Realisations;
